import { ActivatedRoute } from '@angular/router'
import {
  getReissueActivationCodeState,
  getUpdateMobileUserState,
} from '../mobile-users.selectors'
import { Store, select } from '@ngrx/store'
import {
  UpdateField,
  SetDefaultData,
  FetchMobileUserRequest,
  UpdateMobileUserRequest,
  ReissueActivationCodeRequest,
} from '../mobile-users.actions'
import Validator from '@shared/validatorjs/src/validator'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription, Subject, debounceTime } from 'rxjs'
import { Map } from 'immutable'
import { Clipboard } from '@angular/cdk/clipboard'
import {
  FetchLayerFeaturesRequest,
  FetchMobileUserModesRequest,
  ShowSnackbar,
} from '@shared/shared.actions'
import { FetchMobileSettingsRequest } from '@containers/mobile-settings/mobile-settings.actions'
import { getMobileSettingsState } from '@containers/mobile-settings/mobile-settings.selectors'
import {
  getLayerFeaturesState,
  getMobileUserModesState,
} from '@shared/shared.select'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-update-mobile-user',
  templateUrl: './update-mobile-user.component.html',
  styleUrls: ['./update-mobile-user.component.css'],
})
export class UpdateMobileUserComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  private state$: Subscription
  public isLoading: boolean
  public userId: number
  public layerFeatures: any[] = []
  public mobileSettings$: Subscription
  public layerFeatures$: Subscription
  public zonesAutocomplete$: Subscription
  public onZonesAutocomplete$ = new Subject<string>()
  public activeLayerId: any
  public mobileUserModes = []
  public mobileUserModeKeys = []
  public mobileUserModeDefaults = {}
  public mobileUserModes$: Subscription
  public reissueActivationCode$: Subscription
  public codeIssueLoading = false

  constructor(
    private clipboard: Clipboard,
    public store: Store<any>,
    private route: ActivatedRoute,
    private translateService: TranslateService,
  ) {
    super()
  }

  ngOnInit() {
    this.userId = parseInt(this.route.snapshot.paramMap.get('id'), 10)
    this.store.dispatch<any>(new SetDefaultData('updateUser'))

    this.mobileUserModes$ = this.store
      .pipe(select(getMobileUserModesState))
      .subscribe((state) => {
        const modes = state.toJS()

        this.mobileUserModes = modes.map((mode) => ({
          ...mode,
          title: this.translateService.instant(mode.translationKey, {
            defaultValue: mode.title,
          }),
        }))

        this.mobileUserModeKeys = modes.map((mode) => mode.translationKey)
        this.mobileUserModeDefaults = modes.reduce((prev, current) => {
          return { ...prev, [current.translationKey]: current.title }
        }, {})
      })
    this.reissueActivationCode$ = this.store
      .pipe(select(getReissueActivationCodeState))
      .subscribe((state) => {
        this.codeIssueLoading = state.get('isLoading')
      })

    this.zonesAutocomplete$ = this.onZonesAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        this.store.dispatch<any>(
          new FetchLayerFeaturesRequest({
            filters: {
              matchAnd: [
                {
                  field: 'layer.id',
                  value: this.activeLayerId,
                },
              ],
              or: [{ field: 'label', value: keyword ?? '' }],
            },
            pageSize: 100,
          }),
        )
      })

    this.state$ = this.store
      .pipe(select(getUpdateMobileUserState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
      })

    this.layerFeatures$ = this.store
      .pipe(select(getLayerFeaturesState))
      .subscribe((state) => {
        this.layerFeatures = state.get('data').toJS()
      })

    this.mobileSettings$ = this.store
      .pipe(select(getMobileSettingsState))
      .subscribe((state) => {
        this.activeLayerId = state.getIn([
          'data',
          'SETTINGS_MOBILE_RESPONSIBILITY_LAYER',
        ])

        if (this.activeLayerId) {
          this.store.dispatch<any>(
            new FetchLayerFeaturesRequest({
              filters: {
                matchOr: [
                  {
                    field: 'layer.id',
                    value: this.activeLayerId,
                  },
                ],
              },
              pageSize: 50,
            }),
          )
        }
      })

    this.store.dispatch<any>(
      new FetchMobileUserRequest(this.userId, 'updateUser'),
    )
    this.store.dispatch(new FetchMobileSettingsRequest())
    this.store.dispatch(new FetchMobileUserModesRequest())
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.mobileUserModes$.unsubscribe()
    this.mobileSettings$.unsubscribe()
    this.reissueActivationCode$.unsubscribe()

    if (this.layerFeatures$) {
      this.layerFeatures$.unsubscribe()
    }

    this.zonesAutocomplete$.unsubscribe()
  }

  public reissueCode() {
    this.store.dispatch(new ReissueActivationCodeRequest(this.userId))
  }

  public copyToClipboard(text: string) {
    this.clipboard.copy(text)
    this.store.dispatch<any>(
      new ShowSnackbar({ type: 'success', message: 'Copied to clipboard' }),
    )
  }

  public checkboxChange(e: any, name?: string): void {
    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        e.target.checked,
        'updateUser.data',
      ),
    )
  }

  public handleFieldChange(e: any, name: null | string = null): void {
    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        'updateUser.data',
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        'updateUser',
      ),
    )
  }

  validation() {
    const data = this.state.get('data')
    const rules = {
      username: 'required',
      firstName: 'required',
      lastName: 'required',
      mode: 'required',
    }
    const validator = new Validator(
      {
        username: data.get('username'),
        firstName: data.get('firstName'),
        lastName: data.get('lastName'),
        mode: data.get('mode'),
      },
      rules,
    )

    validator.setAttributeNames({
      firstName: 'first name',
      lastName: 'last name',
    })

    return validator
  }

  public onSubmit(e: Event): void {
    e.preventDefault()

    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch(new UpdateMobileUserRequest(this.userId, payload))
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          'updateUser',
        ),
      )
    }
  }
}
